import { HelperRole } from './HelperRole'
import { Person } from './Person'
import { useSupabaseClientStore } from '../../stores/supaBaseClientStore'
import { HELPER_ROLE_MAINHELPER, HELPER_ROLE_ASSISTINGHELPER } from '../constants'
import { User } from '.'
import { useLogger } from '../../composables/useLogger'

const logger = useLogger()

export class Helper {
    id: string
    created_at: string | null
    person_id: string | null
    person: Person | null
    user_id: string | null
    helperroles: HelperRole[] | null

    constructor(id: string) {
        this.id = id
        this.helperroles = null
    }

    static getSupabase() {
        const supabaseClientStore = useSupabaseClientStore()
        return supabaseClientStore.supabase
    }

    static async fromId(helperId: string) {
        const supabase = this.getSupabase();
        const { data, error } = await supabase
            .from('helpers')
            .select('*')
            .eq('id', helperId)
            .single();
        if (error) logger.throwSupabaseError(error, `Failed to get helper with ID ${helperId}`);
        const helper = this.fromDatabaseRow(data);
        return helper;
    }

    static fromDatabaseRow(row: any) {
        const helper = new Helper(row.id);
        helper.created_at = row.created_at;
        helper.person_id = row.person_id;
        helper.user_id = row.user_id;
        return helper;
    }

    static async fromDatabaseRows(rows: any[]) {
        const helpers: Helper[] = [];
        for (const row of rows) {
            const helper = await Helper.fromDatabaseRow(row);
            helpers.push(helper);
        }
        return helpers;
    }

    async getPerson(): Promise<Person> {
        if (!this.person) {
            if (!this.person_id) {
                throw new Error('Helper has no person_id')
            }
            this.person = await Person.fromId(this.person_id)
        }
        return this.person
    }

    async getHelperRoles(): Promise<HelperRole[]> {
        if (!this.helperroles) {
            this.helperroles = await HelperRole.allForHelper(this.id)
        }
        return this.helperroles
    }

    /**
     * Check if the helper has a main helper role
     * @returns true if the helper has a main helper role, false otherwise
     */
    hasMainHelperRole() {
        return this.helperroles?.some(role => role.helperrole === HELPER_ROLE_MAINHELPER)
    }

    /**
     * Get all helper entries for a person with valid helper roles
     * @param personId 
     * @returns Array of helper entries with their roles loaded
     */
    static async getAllHelpersFromPerson(personId: string) {
        const supabase = this.getSupabase();
        const { data: helperData, error: helperError } = await supabase
            .from('helpers')
            .select('*')
            .eq('person_id', personId)
        if (helperError) logger.throwSupabaseError(helperError, `Failed to get helpers for person ${personId}`);
        const helpers = await this.fromDatabaseRows(helperData);
        logger.debug("Helper: getHelpersFromPerson: helpers", { helpers: helpers })
        return helpers;
    }

    static async getAllHelpersFromAuthUser(authUserId: string) {
        const person = await Person.fromAuthId(authUserId)
        if (!person) throw new Error('No person found for auth user');
        return this.getAllHelpersFromPerson(person.id)
    }

    static async getAllHelpersForUser(userId: string) {
        const supabase = this.getSupabase();
        const { data: helperData, error: helperError } = await supabase
            .from('helpers')
            .select('*')
            .eq('user_id', userId)
        if (helperError) logger.throwSupabaseError(helperError, `Failed to get helpers for user ${userId}`);
        const helpers = await this.fromDatabaseRows(helperData);
        logger.debug("Helper: getAllHelpersForUser: helpers", { helpers: helpers })
        return helpers;
    }

    async getAllUsersForHelperPerson() {
        logger.debug("Helper: getAllUsersForHelperPerson: start")
        const person: Person = await this.getPerson()
        const helpersForPerson = await Helper.getAllHelpersFromPerson(person.id)
        const users: User[] = []
        for (const helper of helpersForPerson) {
            const user = await User.fromId(helper.user_id)
            if (user) {
                users.push(user)
            }
        }
        logger.debug("Helper: getAllUsersForHelperPerson: end", { users: users })
        return users
    }

    /**
     * Find the main helper from an array of helpers
     * @param helpers Array of helpers to search through
     * @returns The main helper 
     * @throws Error if no main helper is found
     */
    static async findMainHelper(helpers: Helper[]): Promise<Helper> {
        // Load all roles for all helpers concurrently
        await Promise.all(helpers.map(helper => helper.getHelperRoles()));

        const mainHelper = helpers.find(helper => helper.hasMainHelperRole());

        if (!mainHelper) {
            throw new Error('No main helper found in findMainHelper');
        }

        return mainHelper;
    }

    static async getMainHelperFromUser(userId: string) {
        const helpers = await this.getAllHelpersForUser(userId)
        const mainHelper = this.findMainHelper(helpers);
        return mainHelper;
    }

    static async getMainHelperFromAuthUser(authUserId: string) {
        logger.debug("Helper: getMainHelperFromAuthUser: authUserId", { authUserId: authUserId })
        const helpers = await Helper.getAllHelpersFromAuthUser(authUserId)
        const mainHelper = this.findMainHelper(helpers);
        return mainHelper;
    }

    static async createNewMainHelper(personId: string, userId: string) {
        const supabase = this.getSupabase();
        const { data, error } = await supabase
            .from('helpers')
            .insert({
                person_id: personId,
                user_id: userId
            })
            .select()
            .single();
        if (error) logger.throwSupabaseError(error, `Failed to create main helper for person ${personId} and user ${userId}`);
        const helper = await Helper.fromDatabaseRow(data);
        const mainHelperRole = await HelperRole.createNewMainHelperRole(helper.id)
        helper.helperroles = [mainHelperRole]
        return helper;
    }

    static async delete(helperId: string) {
        const helper = await this.fromId(helperId)
        if (helper.helperroles) {
            for (const helperrole of helper.helperroles) {
                await HelperRole.delete(helperrole.id)
            }
        }
        const supabase = this.getSupabase();
        const { error } = await supabase
            .from('helpers')
            .delete()
            .eq('id', helperId);
        if (error) logger.throwSupabaseError(error, `Failed to delete helper with ID ${helperId}`);
    }

    static async getHelpersFromIds(helperIds: string[]) {
        const helpers: Helper[] = [];
        for (const helperId of helperIds) {
            const helper = await this.fromId(helperId);
            helpers.push(helper);
        }
        return helpers;
    }

    async getUser() {
        if (!this.user_id) return null
        const user = await User.fromId(this.user_id)
        return user
    }

    async getUserPerson() {
        const user = await this.getUser()
        if (!user) return null
        const person = await Person.fromId(user.person_id)
        return person
    }

    static async createNewAssistingHelper(personId: string, userId: string): Promise<Helper> {
        const supabase = this.getSupabase();
        logger.debug('Creating assisting helper:', { personId, userId });

        const { data: existing, error: checkError } = await supabase
            .from('helpers')
            .select('id')
            .eq('person_id', personId)
            .eq('user_id', userId)
            .single();

        if (checkError && checkError.code !== 'PGRST116') { // PGRST116 is "not found" which is what we want
            logger.throwSupabaseError(checkError, `Failed to check for existing helper for person ${personId} and user ${userId}`);
        }

        if (existing) {
            throw new Error('This person is already a helper for this user');
        }

        // Create the helper
        logger.debug('Creating helper:', { personId, userId });
        const { data, error } = await supabase
            .from('helpers')
            .insert({
                person_id: personId,
                user_id: userId
            })
            .select(`
                    id,
                    person_id,
                    user_id
                `)
            .single();

        if (error) logger.throwSupabaseError(error, `Failed to create assisting helper for person ${personId} and user ${userId}`);

        const helper = this.fromDatabaseRow(data);

        // Add the assisting helper role
        logger.debug('Adding helper role:', { helperId: data.id, helperrole: HELPER_ROLE_ASSISTINGHELPER });
        const helperRole = await HelperRole.createNewAssistingHelperRole(data.id);

        if (!helperRole) {
            logger.error('Error adding helper role');
            // Try to clean up the helper if role creation fails
            await supabase.from('helpers').delete().eq('id', data.id);
            throw new Error('Failed to create helper role');
        }

        helper.helperroles = [helperRole]
        logger.debug('Successfully created assisting helper:', { helperId: data.id, personId: data.person_id, userId: data.user_id });
        return helper;
    }

    /**
     * Delete this helper
     * @returns {Promise<void>}
     */
    async delete(): Promise<void> {
        logger.debug('Helper: Deleting helper', { helperId: this.id })
        const { error } = await Helper.getSupabase()
            .from('helpers')
            .delete()
            .eq('id', this.id)

        if (error) {
            logger.error('Helper: Error deleting helper', { error, helperId: this.id })
            throw error
        }

        logger.debug('Helper: Helper deleted', { helperId: this.id })
    }
}
